import { clsx } from '@alexis/helpers/clsx';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import BusinessIcon from '@icons/business.svg';
import ChevronDown from '@icons/chevron_down_small.svg';
import CoupleIcon from '@icons/couple.svg';
import FamilyIcon from '@icons/family.svg';
import FilterIcon from '@icons/filter_list.svg';
import SoloIcon from '@icons/solo.svg';
import WorldIcon from '@icons/world.svg';

import { getTranslationsState } from '@redux/selectors';

import { HotelSortType } from '@wegoTypes/hotels/hotelSortType';

import styles from './HotelSort.module.scss';

interface HotelSortProps {
  onSortDropdownOpen(): void;
  onSortTypeChanged(sortType: HotelSortType, sortOrder: 'asc' | 'desc'): void;
  selectedSortOrder: 'asc' | 'desc';
  selectedSortType: HotelSortType;
}

const HotelSort: React.FC<HotelSortProps> = ({
  onSortDropdownOpen,
  onSortTypeChanged,
  selectedSortOrder,
  selectedSortType,
}) => {
  const translations = useSelector(getTranslationsState);

  const [isPriceExpanded, setIsPriceExpanded] = useState<boolean>(false);
  const [isGuestRatingExpanded, setIsGuestRatingExpanded] = useState<boolean>(false);
  const [isDistanceExpanded, setIsDistanceExpanded] = useState<boolean>(false);
  const [searchParams] = useSearchParams();

  const radiusLatParam = searchParams.get('lat');
  const radiusLongParam = searchParams.get('long');

  // Set all picker focus to false if body is clicked
  useEffect(() => {
    if (isPriceExpanded || isGuestRatingExpanded || isDistanceExpanded) {
      const handleBodyOnClick = (): void => {
        if (isPriceExpanded) {
          setIsPriceExpanded(false);
        }

        if (isGuestRatingExpanded) {
          setIsGuestRatingExpanded(false);
        }

        if (isDistanceExpanded) {
          setIsDistanceExpanded(false);
        }
      };

      document.body.addEventListener('click', handleBodyOnClick);

      return () => {
        document.body.removeEventListener('click', handleBodyOnClick);
      };
    }
  }, [isPriceExpanded, isGuestRatingExpanded, isDistanceExpanded]);

  const handleOnDropdownButtonClick = useCallback<
    (dropdownType: 'price' | 'guestRating' | 'distance') => void
  >(
    (dropdownType: 'price' | 'guestRating' | 'distance') => {
      switch (dropdownType) {
        case 'price':
          setIsPriceExpanded(!isPriceExpanded);
          break;
        case 'guestRating':
          setIsGuestRatingExpanded(!isGuestRatingExpanded);
          break;
        case 'distance':
          setIsDistanceExpanded(!isDistanceExpanded);
          break;
      }
      onSortDropdownOpen();
    },
    [isPriceExpanded, isGuestRatingExpanded, isDistanceExpanded],
  );

  const handleOnSortTypeChanged = useCallback<
    (sortType: HotelSortType, sortOrder: 'asc' | 'desc') => void
  >(
    (sortType: HotelSortType, sortOrder: 'asc' | 'desc') => {
      setIsPriceExpanded(false);
      setIsGuestRatingExpanded(false);
      setIsDistanceExpanded(false);

      onSortTypeChanged(sortType, sortOrder);
    },
    [onSortTypeChanged],
  );

  return (
    <div className={styles.container}>
      {/* Recommened sorting */}
      <div
        className={clsx(
          styles.sortItem,
          selectedSortType === HotelSortType.Recommended && styles.active,
        )}
        onClick={() => onSortTypeChanged(HotelSortType.Recommended, 'desc')}
      >
        <div className={styles.header}>{translations.recommended}</div>
      </div>

      {/* Price sorting */}
      <div
        className={clsx(
          styles.sortItem,
          (selectedSortType === HotelSortType.Price ||
            selectedSortType === HotelSortType.Discount ||
            selectedSortType === HotelSortType.Saving) &&
            styles.active,
          isPriceExpanded && styles.expanded,
        )}
        onClick={() => handleOnDropdownButtonClick('price')}
      >
        <div className={styles.header}>{translations.price}</div>

        <ChevronDown className={styles.chevron} />

        {/* Price dropdown */}
        {isPriceExpanded ? (
          <div className={styles.dropdown} onClick={(e) => e.stopPropagation()}>
            <div className={styles.subHeader}>{translations.sort_by}</div>

            <div
              className={clsx(
                styles.item,
                selectedSortType === HotelSortType.Price &&
                  selectedSortOrder === 'asc' &&
                  styles.active,
              )}
              onClick={() => handleOnSortTypeChanged(HotelSortType.Price, 'asc')}
            >
              {translations.lowest_price_sort}
            </div>
            <div
              className={clsx(
                styles.item,
                selectedSortType === HotelSortType.Price &&
                  selectedSortOrder === 'desc' &&
                  styles.active,
              )}
              onClick={() => handleOnSortTypeChanged(HotelSortType.Price, 'desc')}
            >
              {translations.highest_price_sort}
            </div>
            <div
              className={clsx(
                styles.item,
                selectedSortType === HotelSortType.Discount &&
                  selectedSortOrder === 'desc' &&
                  styles.active,
              )}
              onClick={() => handleOnSortTypeChanged(HotelSortType.Discount, 'desc')}
            >
              {translations.best_discount}
            </div>
            <div
              className={clsx(
                styles.item,
                selectedSortType === HotelSortType.Saving &&
                  selectedSortOrder === 'desc' &&
                  styles.active,
              )}
              onClick={() => handleOnSortTypeChanged(HotelSortType.Saving, 'desc')}
            >
              {translations.biggest_savings}
            </div>
          </div>
        ) : null}
      </div>

      {/* Guest rating sorting */}
      <div
        className={clsx(
          styles.sortItem,
          (selectedSortType === HotelSortType.AllReviewScore ||
            selectedSortType === HotelSortType.SoloReviewScore ||
            selectedSortType === HotelSortType.BusinessReviewScore ||
            selectedSortType === HotelSortType.FamilyReviewScore ||
            selectedSortType === HotelSortType.CoupleReviewScore) &&
            styles.active,
          isGuestRatingExpanded && styles.expanded,
        )}
        onClick={() => handleOnDropdownButtonClick('guestRating')}
      >
        <div className={styles.header}>{translations.guest_rating}</div>

        <ChevronDown className={styles.chevron} />

        {/* Guest rating dropdown */}
        {isGuestRatingExpanded ? (
          <div className={styles.dropdown} onClick={(e) => e.stopPropagation()}>
            <div className={styles.subHeader}>{translations.highest_rated}</div>

            <div
              className={clsx(
                styles.item,
                selectedSortType === HotelSortType.AllReviewScore &&
                  selectedSortOrder === 'desc' &&
                  styles.active,
              )}
              onClick={() => handleOnSortTypeChanged(HotelSortType.AllReviewScore, 'desc')}
            >
              <WorldIcon className={styles.icon} />
              {translations.all_travellers}
            </div>
            <div
              className={clsx(
                styles.item,
                selectedSortType === HotelSortType.SoloReviewScore &&
                  selectedSortOrder === 'desc' &&
                  styles.active,
              )}
              onClick={() => handleOnSortTypeChanged(HotelSortType.SoloReviewScore, 'desc')}
            >
              <SoloIcon className={styles.icon} />
              {translations.solo_travellers}
            </div>
            <div
              className={clsx(
                styles.item,
                selectedSortType === HotelSortType.BusinessReviewScore &&
                  selectedSortOrder === 'desc' &&
                  styles.active,
              )}
              onClick={() => handleOnSortTypeChanged(HotelSortType.BusinessReviewScore, 'desc')}
            >
              <BusinessIcon className={styles.icon} />
              {translations.business_travellers}
            </div>
            <div
              className={clsx(
                styles.item,
                selectedSortType === HotelSortType.FamilyReviewScore &&
                  selectedSortOrder === 'desc' &&
                  styles.active,
              )}
              onClick={() => handleOnSortTypeChanged(HotelSortType.FamilyReviewScore, 'desc')}
            >
              <FamilyIcon className={styles.icon} />
              {translations.family_travellers}
            </div>
            <div
              className={clsx(
                styles.item,
                selectedSortType === HotelSortType.CoupleReviewScore &&
                  selectedSortOrder === 'desc' &&
                  styles.active,
              )}
              onClick={() => handleOnSortTypeChanged(HotelSortType.CoupleReviewScore, 'desc')}
            >
              <CoupleIcon className={styles.icon} />
              {translations.couple_travellers}
            </div>
          </div>
        ) : null}
      </div>

      {/* Distance rating sorting */}
      <div
        className={clsx(
          styles.sortItem,
          (selectedSortType === HotelSortType.NearestToAirport ||
            selectedSortType === HotelSortType.NearestToCityCentre ||
            selectedSortType === HotelSortType.NearestToTargetLocation) &&
            styles.active,
          isDistanceExpanded && styles.expanded,
        )}
        onClick={() => handleOnDropdownButtonClick('distance')}
      >
        <div className={styles.header}>{translations.distance}</div>

        <ChevronDown className={styles.chevron} />

        {/* Distance dropdown */}
        {isDistanceExpanded ? (
          <div className={styles.dropdown} onClick={(e) => e.stopPropagation()}>
            <div className={styles.subHeader}>{translations.nearest_to}</div>

            <div
              className={clsx(
                styles.item,
                selectedSortType === HotelSortType.NearestToCityCentre &&
                  selectedSortOrder === 'asc' &&
                  styles.active,
              )}
              onClick={() => handleOnSortTypeChanged(HotelSortType.NearestToCityCentre, 'asc')}
            >
              {translations.city_centre}
            </div>
            <div
              className={clsx(
                styles.item,
                selectedSortType === HotelSortType.NearestToAirport &&
                  selectedSortOrder === 'asc' &&
                  styles.active,
              )}
              onClick={() => handleOnSortTypeChanged(HotelSortType.NearestToAirport, 'asc')}
            >
              {translations.airport}
            </div>
            {radiusLatParam && radiusLongParam ? (
              <div
                className={clsx(
                  styles.item,
                  selectedSortType === HotelSortType.NearestToTargetLocation &&
                    selectedSortOrder === 'asc' &&
                    styles.active,
                )}
                onClick={() =>
                  handleOnSortTypeChanged(HotelSortType.NearestToTargetLocation, 'asc')
                }
              >
                {translations.target_location}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      {/* Stars sorting */}
      <div
        className={clsx(styles.sortItem, selectedSortType === HotelSortType.Star && styles.active)}
        onClick={() =>
          onSortTypeChanged(
            HotelSortType.Star,
            selectedSortType !== HotelSortType.Star
              ? 'desc'
              : selectedSortOrder === 'desc'
              ? 'asc'
              : 'desc',
          )
        }
      >
        <div className={styles.header}>
          {selectedSortType !== HotelSortType.Star
            ? translations.stars
            : selectedSortType === HotelSortType.Star && selectedSortOrder === 'desc'
            ? translations.stars_five
            : translations.stars_one}
        </div>

        <FilterIcon
          className={clsx(
            styles.icon,
            selectedSortType === HotelSortType.Star &&
              selectedSortOrder === 'asc' &&
              styles.ascending,
          )}
        />
      </div>
    </div>
  );
};

export default HotelSort;
